import { Validators } from '@angular/forms';
import {
  FormField,
  FormFieldList,
  SfAmountInputFormField,
  SfCheckboxFormField,
  SfDatePickerFormField,
  SfFileUploadFormField,
  SfInputFormField,
  SfSelectData,
  SfSelectFormField,
  SfSelectOption,
  SfTextAreaFormField,
  SfToggleFormField,
} from 'ngx-smart-form';
import { IFormField } from 'ngx-smart-form/lib/smart-form/interfaces/i-form-field';
import { MaxLengthCounterField } from '../../history/max-lenght-counter-field';
import { Finanziamenti } from '../../requests/models/cms-finanziamenti';
import { CategoryUploadField } from './category-upload-field';

export interface ICmsField {
  element: {
    description: string;
    value: string;
  }[];
  informations: string | undefined;
  label: string;
  name: string;
  placeholder: string;
  required: boolean;
  type: CmsFieldType;
  parent?: string;
  parentMatchValue?: any;
  title?: string;
  description?: string;
  default?: { value?: string; description?: string };
  slice?: number;
  decimals?: number;
  currency?: string;
  disabled?: boolean;
  max?: number | string;
  min?: number | string;
  category?: string;
}

export type ExtendedField = IFormField & SfSelectData & {category?: string};

const parseBoolean = (value: string | undefined) => {
  if (value === 'true' || value === 'false') {
    return value === 'true';
  } else {
    return value;
  }
};

export class CmsField {
  static fieldTypeMap = new Map<
    CmsFieldType,
    (data: ExtendedField) => FormField
  >([
    [
      'AREA',
      (fieldData: ExtendedField) => new MaxLengthCounterField(fieldData, 4000),
    ],
    [
      'CHECKBOX',
      (fieldData: ExtendedField) => new SfCheckboxFormField(fieldData),
    ],
    [
      'DATEPICKER',
      (fieldData: ExtendedField) => new SfDatePickerFormField(fieldData),
    ],
    [
      'NUMBER',
      (fieldData: ExtendedField) => new SfAmountInputFormField(fieldData),
    ],
    ['TEXT', (fieldData: ExtendedField) => new SfInputFormField(fieldData)],
    ['SELECT', (fieldData: ExtendedField) => new SfSelectFormField(fieldData)],
    ['TOGGLE', (fieldData: ExtendedField) => new SfToggleFormField(fieldData)],
    ['UPLOAD', (fieldData: ExtendedField) => new CategoryUploadField(fieldData)],
  ]);
  static genField<T>(field: ICmsField, item?: T) {
    const createField = CmsField.fieldTypeMap.get(field.type);

    let options: SfSelectOption[] = [];
    if (field.type === 'SELECT' && field.element) {
      options = field.element.map((o) => ({
        view: o.description,
        value: o.value,
      }));
    }
    let max = field.max !== undefined ? Number(field.max) : undefined;
    max = !max || isNaN(max) ? undefined : max;

    const newField: (FormField & { slice?: number }) | undefined = createField
      ? createField({
          ...field,
          formControlName: field.name,
          value: item ? item[field.name as keyof T] : field.default?.value,
          disabled:
            field.disabled !== undefined
              ? field.disabled
              : !!field.default?.value,
          info: field.informations,
          options,
          parentMatchValue: parseBoolean(field.parentMatchValue),
          validators: max !== undefined ? [Validators.max(max)] : undefined,
        })
      : undefined;

    if (newField) {
      newField.slice = field.slice;
    }
    return newField;
  }

  static genFieldList<T>(cmsFields: ICmsField[], item?: T) {
    let fields = cmsFields
      .map((el) => {
        return CmsField.genField<T>(el, item);
      })
      .filter((el) => !!el) as (FormField<T> & { slice?: number })[];
    let sliced: FormFieldList = [];
    for (let i = 0; i < fields.length; i++) {
      let slice = fields.slice(i, i + (fields[i].slice || 2));
      if (
        Finanziamenti.isStandaloneField(slice[0]) ||
        Finanziamenti.isStandaloneField(slice[1])
      ) {
        slice.pop();
        sliced.push(slice);
      } else {
        sliced.push(slice);
        i = i + slice.length - 1;
      }
    }
    return sliced;
  }
}

export type CmsFieldType =
  | 'NUMBER'
  | 'TEXT'
  | 'CHECKBOX'
  | 'TOGGLE'
  | 'RADIO'
  | 'SELECT'
  | 'DATEPICKER'
  | 'UPLOAD'
  | 'AREA';
